import {
  ActionFunctionArgs,
  Form,
  Link,
  LoaderFunctionArgs,
  redirect,
  useFetcher,
  useLoaderData,
  useNavigate,
} from "react-router-dom";
import toast from "react-hot-toast";
import { Input } from "../../components/inputs/Input";
import TicketingItemHTML from "../tickets/components/TickingItemHTML";
import CustomSelect from "../../components/customSelect";
import { FormLayout } from "../../layout/FormLayout";
import {
  organizationStore,
  useOrganizationStore,
} from "../../store/organization";
import { DatePicker } from "../../components/DatePicker";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { GET_EVENT } from "./queries";
import { UPDATE_EVENT, UPDATE_EVENT_MERCH_SETTINGS } from "./mutation";
import { Event } from "../../types/event";
import {
  GET_LIST_PAYMENT_PROCESSORS_BY_ORGANIZATION,
  GET_LIST_VENDORS_BY_ORGANIZATION,
} from "../../queries";
import { PaymentProcessor } from "../../types/paymentProcessor";
import { Vendor } from "../../types/vendor";
import { parseDate } from "@internationalized/date";
import IndeterminateCheckbox from "../../components/inputs/IndeterminantCheckbox";
import {
  cashRefundErrors,
  centsToDollars,
  configOf,
  dollarsToCents,
  type CashRefundConfig,
  type RefundEvent,
} from "../refund-manager/rules";
import { TagsInput } from "../../components/inputs/TagsInput";
import { Fragment, useRef, useState } from "react";
import { usePermissions } from "../auth/api";
import PercentageInput from "../../components/inputs/PercentageInput";
import DeleteModal from "../../components/DeleteModal";
import {SecondaryButton, TertiaryButton} from "../../components/Button";
import { userStore } from "../../store/user";
import {Tab} from "../../components/Tab";
import PriceInput from "../../components/inputs/PriceInput";
import CompactThumbUploader from "../../components/CompactThumbUploader";
import DemographicQuestionsBuilder from "../../components/DemographicQuestionsBuilder";
import SubEventManager, { SubEventManagerHandle } from "../../components/SubEventManager";
import { MerchVendorDeals } from "./MerchVendorDeals";
import { Dialog, Transition } from "@headlessui/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { GET_IMAGE_LIBRARY_ITEMS_LIMITED } from "../image-library/queries";
import { ImageLibrary } from "../../types/item";
import {
  REMAINING_DUE_BUTTON_IDS,
  REMAINING_DUE_BUTTON_DISPLAY_NAMES,
  mergeRemainingDueButtons,
  type RemainingDueButtonId,
  type RemainingDueButtons,
} from "./remainingDueButtonsConfig";
type PaymentType = "card" | "cash" | "rfid" | "qr";

// ─────────────────────────────────────────────────────────────────────────────
// KILL SWITCH — Inventory Configuration (Merch Manager) tab.
//
// POS builds in the field (<= build 471) crash their post-login sync on any
// ARRAY value inside events.configuration: Realm's dictionary<mixed> rejects
// plain JS arrays, the thrown error is swallowed, and every tablet organizer
// login for the org spins for half a second and silently gives up. The
// ItemCategoryPicker in this tab writes merch_item_categories as string[], so
// ONE ticked checkbox by ops bricks the org's tablet logins with no error.
//
// The tablet fix (stringify arrays/objects before the Realm write, in
// posapp/src/services/new_sync/newSyncEvents.js) is committed but NOT on the
// fleet yet. Keep this tab hidden until a POS build > 471 containing that fix
// has rolled out via version5.json — then set this to true to re-enable.
// ─────────────────────────────────────────────────────────────────────────────
const SHOW_INVENTORY_CONFIGURATION_TAB = false;

const client = createApolloClient();

const { getState } = organizationStore;
const { getState: getUserStore } = userStore;

export const eventEditLoader = async ({ params }: LoaderFunctionArgs) => {
  const organizationId = getState().organizationId;
  const { id } = params;
  const [
    { data: eventData },
    { data: paymentProcessors },
    { data: vendorData },
    { data: imageData },
  ] = await Promise.all([
    client.query<{ events_by_pk: Event }>({
      query: GET_EVENT,
      variables: { id },
    }),
    client.query({
      query: GET_LIST_PAYMENT_PROCESSORS_BY_ORGANIZATION,
      variables: {
        where: organizationId
          ? {
              organization_id: {
                _eq: organizationId,
              },
            }
          : {},
      },
      fetchPolicy: "network-only",
    }),
    client.query({
      query: GET_LIST_VENDORS_BY_ORGANIZATION,
      variables: {
        where: organizationId
          ? {
              organization_id: {
                _eq: organizationId,
              },
            }
          : {},
      },
    }),
    client.query({
      query: GET_IMAGE_LIBRARY_ITEMS_LIMITED,
      variables: {
        where: organizationId
          ? {
              organization_id: {
                _eq: organizationId,
              },
              is_active: {
                _eq: true,
              },
            }
          : {},
      },
    }),
  ]);
  return {
    event: eventData.events_by_pk,
    paymentProcessors: paymentProcessors.payment_processor_config,
    vendors: vendorData.vendors,
    images: imageData.image_library,
  };
};

export const eventEditAction = async ({
  params,
  request,
}: ActionFunctionArgs) => {
  const user = getUserStore().user;
  const { id } = params;
  const body = await request.formData();
  const slug = body.get("slug");
  if (slug === "") {
    body.delete("slug");
  } else if (typeof slug === "string") {
    const lowerCaseSlug = slug.trim().toLowerCase();
    body.set("slug", lowerCaseSlug);
  }
  let timezonediffs = 0;
  const tz = body.get("timezone");
  if (tz === "ADT") {
    timezonediffs = -3;
  } else if (tz === "AST") {
    timezonediffs = -4;
  } else if (tz === "EDT") {
    timezonediffs = -4;
  } else if (tz === "EST") {
    timezonediffs = -5;
  } else if (tz === "CDT") {
    timezonediffs = -5;
  } else if (tz === "CST") {
    timezonediffs = -6;
  } else if (tz === "MDT") {
    timezonediffs = -6;
  } else if (tz === "MST") {
    timezonediffs = -7;
  } else if (tz === "PDT") {
    timezonediffs = -7;
  } else if (tz === "PST") {
    timezonediffs = -8;
  } else {
    timezonediffs = 0;
  }
  const tokensString = body.get("available_tokens") as string;
  const available_tokens = tokensString.split(",").map((tag) => tag.trim());
  const zonesString = body.get("location_zones") as string;
  const location_zones = zonesString.split(",").map((tag) => tag.trim());
  const paymentTypes: PaymentType[] = body.getAll(
    "payment_types"
  ) as PaymentType[];
  body.delete("image_library");
  const signupQuestionsRaw = body.get("signup_questions") as string;
  const signup_questions = signupQuestionsRaw ? JSON.parse(signupQuestionsRaw) : null;
  body.delete("signup_questions");
  const subEventsRaw = body.get("sub_events") as string;
  const sub_events = subEventsRaw ? JSON.parse(subEventsRaw) : null;
  body.delete("sub_events");
  const is_asset_transfer_combine = body.get("is_asset_transfer_combine") === "true";
  body.delete("is_asset_transfer_combine");
  const showRemainingCta = body.get("ticketing_show_remaining_cta") === "true";
  body.delete("ticketing_show_remaining_cta");
  const ticketing_configurations = { show_remaining_cta: showRemainingCta };
  const pinOnAssociationOnly = body.get("cashless_pin_on_association_only") === "true";
  body.delete("cashless_pin_on_association_only");
  const allowVaultCardSkip = body.get("cashless_allow_vault_card_skip") === "true";
  body.delete("cashless_allow_vault_card_skip");
  const enableDownloadableQr = body.get("cashless_enable_downloadable_qr") === "true";
  body.delete("cashless_enable_downloadable_qr");
  // Lets the event stay active in the dashboard while being hidden from the
  // tablet's event-picker dropdown so cashiers don't accidentally select it.
  const hideFromPosDropdown = body.get("cashless_hide_from_pos_dropdown") === "true";
  body.delete("cashless_hide_from_pos_dropdown");
  const registrationHeaderRaw = body.get("cashless_registration_header");
  body.delete("cashless_registration_header");
  const registrationHeader = typeof registrationHeaderRaw === "string" ? registrationHeaderRaw.trim() : "";
  // Remaining Due button configs (label/color/position for each of the 3 buttons).
  // The hidden input carries the full JSON; the per-row label inputs are only
  // there to keep React's controlled-input contract clean — we delete those names
  // from FormData so they don't get spread into the mutation as stray columns.
  const remainingDueButtonsRaw = body.get("cashless_remaining_due_buttons") as string | null;
  body.delete("cashless_remaining_due_buttons");
  for (const id of REMAINING_DUE_BUTTON_IDS) {
    body.delete(`remaining_due_btn_${id}_label`);
  }
  let remaining_due_buttons: RemainingDueButtons | null = null;
  if (remainingDueButtonsRaw) {
    try {
      remaining_due_buttons = mergeRemainingDueButtons(JSON.parse(remainingDueButtonsRaw));
    } catch {
      remaining_due_buttons = null;
    }
  }
  // This form owns six keys of cashless_configurations, but the column holds
  // others too — cash_refund above all. Writing only the six would wipe
  // everything else on every save: edit an event's name and its cash refunds
  // silently switch off. So start from what is stored and overlay the form.
  // If the read fails, refuse to save rather than guess.
  let storedCashless: Record<string, unknown> = {};
  try {
    const { data } = await client.query<{ events_by_pk: Event }>({
      query: GET_EVENT,
      variables: { id },
      fetchPolicy: "network-only",
    });
    storedCashless = (data?.events_by_pk?.cashless_configurations as Record<string, unknown>) || {};
  } catch (error) {
    toast.error("Could not load the event's current settings, so nothing was saved: " + error);
    return null;
  }
  // Cash refunds. The section only renders for Ronin staff, so an ordinary
  // save carries none of these fields - and must leave whatever is stored
  // exactly as it was rather than reading absent inputs as "off".
  let cash_refund = storedCashless.cash_refund as CashRefundConfig | undefined;
  if (body.get("cash_refund_present") === "true") {
    const enabled = body.get("cash_refund_enabled") === "true";

    // The settings only render when refunds are ON. With them off the form
    // carries the checkbox and nothing else, so reading absent inputs as blank
    // meant every save of every event with refunds off failed validation on a
    // fee nobody had been shown. Absent is "unchanged", not "empty".
    const stored = (cash_refund || {}) as CashRefundConfig;
    const submitted = body.get("cash_refund_fee") !== null;

    const feeCents = submitted
      ? dollarsToCents((body.get("cash_refund_fee") as string) ?? "")
      : Number(stored.fee_amount ?? 0);
    const minimumCents = submitted
      ? dollarsToCents((body.get("cash_refund_minimum") as string) ?? "")
      : Number(stored.min_amount ?? 100);
    const secCode = submitted
      ? ((body.get("cash_refund_sec_code") as string) === "PPD" ? "PPD" : "CCD")
      : (stored.ach?.sec_code === "PPD" ? "PPD" : "CCD");
    const entryDescription = (
      submitted
        ? String((body.get("cash_refund_entry_description") as string) || "REFUND")
        : String(stored.ach?.entry_description || "REFUND")
    )
      .toUpperCase()
      .slice(0, 10);
    const effectiveDays = submitted
      ? Math.min(30, Math.max(1, Number(body.get("cash_refund_effective_days")) || 1))
      : Math.min(30, Math.max(1, Number(stored.ach?.effective_days) || 1));

    // Only what the GM was actually shown can be wrong.
    if (submitted) {
      const problems = cashRefundErrors({ feeCents, minimumCents, secCode, entryDescription });
      if (problems.length) {
        toast.error(problems[0]);
        return null;
      }
    }

    // Blank means "use the refund page's own wording", so an emptied field is
    // stored as null rather than as an empty heading.
    const copyField = (key: string) =>
      body.get(key) === null
        ? ((stored.copy as Record<string, string | null> | undefined)?.[key.replace("cash_refund_", "")] ?? null)
        : String((body.get(key) as string) || "").trim() || null;
    const copyHeading = copyField("cash_refund_heading");
    const copyIntro = copyField("cash_refund_intro");

    // Unchecking hides the amounts, it does not throw them away: turn refunds
    // back on and the fee and minimum are as they were left.
    cash_refund = {
      ...(cash_refund || {}),
      enabled,
      copy: {
        heading: copyHeading,
        intro: copyIntro,
        step_one: copyField("cash_refund_step_one"),
        step_two: copyField("cash_refund_step_two"),
        step_three: copyField("cash_refund_step_three"),
        uid_label: copyField("cash_refund_uid_label"),
        uid_placeholder: copyField("cash_refund_uid_placeholder"),
      },
      ach: {
        ...((cash_refund?.ach as Record<string, unknown>) || {}),
        sec_code: secCode,
        entry_description: entryDescription,
        effective_days: effectiveDays,
      },
      fee_amount: feeCents ?? 0,
      min_amount: minimumCents ?? 100,
    };
  }

  // These are cashless_configurations.cash_refund, not columns on events. Every
  // field left in the form body is sent to the update mutation as an event
  // column, so anything handled here has to be removed - the same reason the
  // web-checkout and merch fields below are deleted.
  [
    "cash_refund_present",
    "cash_refund_enabled",
    "cash_refund_enabled_visible",
    "cash_refund_fee",
    "cash_refund_minimum",
    "cash_refund_sec_code",
    "cash_refund_entry_description",
    "cash_refund_effective_days",
    "cash_refund_heading",
    "cash_refund_intro",
    "cash_refund_step_one",
    "cash_refund_step_two",
    "cash_refund_step_three",
    "cash_refund_uid_label",
    "cash_refund_uid_placeholder",
  ].forEach((field) => body.delete(field));

  const cashless_configurations = {
    ...storedCashless,
    ...(cash_refund ? { cash_refund } : {}),
    pin_on_association_only: pinOnAssociationOnly,
    allow_vault_card_skip: allowVaultCardSkip,
    enable_downloadable_qr: enableDownloadableQr,
    hide_from_pos_dropdown: hideFromPosDropdown,
    registration_header: registrationHeader || null,
    remaining_due_buttons,
  };
  const webCheckoutProcessorId = body.get("web_checkout_payment_processor_id") as string;
  const webCheckoutSlug = body.get("web_checkout_slug") as string;
  const webCheckoutLogo = body.get("web_checkout_logo") as string;
  const webCheckoutBackgroundUrl = body.get("web_checkout_background_url") as string;
  const webCheckoutServiceFeePercentage = body.get("web_checkout_service_fee_percentage") as string;
  const webCheckoutDescription = body.get("web_checkout_description") as string;
  const webCheckoutCollectClientInfo = body.get("web_checkout_collect_client_info") === "true";
  body.delete("web_checkout_payment_processor_id");
  body.delete("web_checkout_slug");
  body.delete("web_checkout_logo");
  body.delete("web_checkout_background_url");
  body.delete("web_checkout_service_fee_percentage");
  body.delete("web_checkout_description");
  body.delete("web_checkout_collect_client_info");
  const web_checkout_configurations = {
    payment_processor_id: webCheckoutProcessorId || null,
    slug: webCheckoutSlug || null,
    logo: webCheckoutLogo || null,
    background_url: webCheckoutBackgroundUrl || null,
    service_fee_percentage: webCheckoutServiceFeePercentage || 0,
    description: webCheckoutDescription || null,
    // When false the public page collects only the amount (no client name/phone/email)
    collect_client_info: webCheckoutCollectClientInfo,
  };
  // Merch Manager settings live in the shared `configuration` JSONB, so they
  // CANNOT ride along in the _set spread below (that would replace the whole
  // column and wipe order-ahead's keys) — pull them out here and write them
  // with a separate `_append` merge after the main update.
  const merchPayoutSourceRaw = body.get("merch_payout_source");
  const merchTaxPercentageRaw = body.get("merch_tax_percentage");
  const merchTaxTypeRaw = body.get("merch_tax_type");
  const merchSettingsSubmitted = merchPayoutSourceRaw !== null;
  body.delete("merch_payout_source");
  body.delete("merch_tax_percentage");
  body.delete("merch_tax_type");

  const merchPayoutSource =
    typeof merchPayoutSourceRaw === "string" && merchPayoutSourceRaw !== ""
      ? merchPayoutSourceRaw
      : null;
  // Stored AS ENTERED (7.5 = 7.5%). Merch Manager converts to the fraction the
  // items table expects (0.075) when it stamps a new item.
  const merchTaxPercentageParsed = parseFloat(String(merchTaxPercentageRaw ?? ""));
  const merchTaxPercentage = Number.isFinite(merchTaxPercentageParsed)
    ? merchTaxPercentageParsed
    : 0;
  const merchTaxType = merchTaxTypeRaw === "exclusive" ? "exclusive" : "inclusive";

  const perOrderFeeString = body.get("ticketing_service_fee_per_order") as string;;
  const perOrderFeeFloat = parseFloat(perOrderFeeString);
  const perOrderFeeInt = Math.round(perOrderFeeFloat * 100);
  const bodyData = Object.fromEntries(body);
  try {
    await client.mutate({
      mutation: UPDATE_EVENT,
      variables: {
        id,
        input: {
          ...bodyData,
          available_tokens,
          location_zones,
          payment_types: paymentTypes,
          start_date: bodyData?.start_date || null,
          end_date: bodyData?.end_date || null,
          frontgate_site_id: bodyData?.frontgate_site_id || null,
          payment_processor_id: bodyData?.payment_processor_id || null,
          ticketing_payment_processor_id: bodyData?.ticketing_payment_processor_id || null,
          invoicing_payment_processor_id: bodyData?.invoicing_payment_processor_id || null,
          timezone_diff: timezonediffs,
          web_surcharge_percentage: bodyData?.web_surcharge_percentage || 0,
          ticketing_service_fee_per_order: perOrderFeeInt,
          signup_questions,
          sub_events,
          is_asset_transfer_combine,
          ticketing_configurations,
          cashless_configurations,
          web_checkout_configurations,
          last_updated_by: user?.id,
        },
      },
    });
    // Only touch the JSONB when the fields were actually part of this
    // submission — absent fields must never silently clear configured values.
    if (merchSettingsSubmitted) {
      await client.mutate({
        mutation: UPDATE_EVENT_MERCH_SETTINGS,
        variables: {
          eventId: Number(id),
          patch: {
            merch_payout_source: merchPayoutSource,
            merch_tax_percentage: merchTaxPercentage,
            merch_tax_type: merchTaxType,
          },
        },
      });
    }
    toast.success("Event updated successfully");
    return redirect("/events/list");
  } catch (error) {
    toast.error("Error updating event: " + error);
  }
};

export const EventEdit = () => {
  const navigate = useNavigate();
  const organizations = useOrganizationStore((state) => state.organizations);
  const permissions = sessionStorage.getItem("hasuraDefaultRole");
  const { event, paymentProcessors, images } = useLoaderData() as {
    event: Event;
    paymentProcessors: PaymentProcessor[];
    vendors: Vendor[];
    images: ImageLibrary[];
  };
  const imageOptions = images.map((item: { image_base64: string; display_name: string }) => ({
    value: item.image_base64,
    label: item.display_name,
    display_name: item.display_name,
    image_base64: item.image_base64,
  }));
  const defaultStartDate = event.start_date
    ? parseDate(event.start_date)
    : null;
  const defaultEndDate = event.end_date ? parseDate(event.end_date) : null;
  const [slugInputValue, setSlugInputValue] = useState(event.slug);
  const [ticketingSlugInputValue, setTicketingSlugInputValue] = useState(
    event.ticketing_slug
  );
  const [webCheckoutSlugInputValue, setWebCheckoutSlugInputValue] = useState(
    event.web_checkout_configurations?.slug ?? ""
  );
  const sortedPaymentProcessors = [...paymentProcessors]
    .filter((p) => p.is_active !== false)
    .sort((a, b) => a.payment_processor.localeCompare(b.payment_processor));
  const [logoutProtected, setLogoutProtected] = useState(
    event?.is_org_logout_protected
  );
  const { deletePermission } = usePermissions("events");
  const fetcher = useFetcher();
  const handleClone = () => {
    navigate(`/events/${event.id}/clone`);
  };

  const [serviceFeePerOrder, setServiceFeePerOrder] = useState<number>(event?.ticketing_service_fee_per_order / 100);
  const [serviceFeePerOrderTopUp, setServiceFeePerOrderTopUp] = useState<number>(event?.top_up_service_fee_per_order / 100);
  const [eventLogoBase64, setEventLogoBase64] = useState<string>(event?.event_logo_base64 ?? "");
  const [descriptionContent, setDescriptionContent] = useState<string>(event?.description ?? "");
  const [webCheckoutDescriptionContent, setWebCheckoutDescriptionContent] = useState<string>(event?.web_checkout_configurations?.description ?? "");
  const [remainingDueButtons, setRemainingDueButtons] = useState<RemainingDueButtons>(() =>
    mergeRemainingDueButtons(event?.cashless_configurations?.remaining_due_buttons)
  );

  // Cash refunds pay out of RONIN's bank account, so only Ronin staff decide
  // that an event's attendees can be paid. Everyone else does not see the
  // setting at all, and their saves leave it untouched.
  const isRoninStaff = getUserStore().user?.role_id === 1;
  const storedCashRefund = configOf(event as unknown as RefundEvent);
  const [cashRefundEnabled, setCashRefundEnabled] = useState<boolean>(
    storedCashRefund?.enabled === true
  );

  const updateRemainingDueButtonField = (
    id: RemainingDueButtonId,
    field: "label" | "color",
    value: any
  ) => {
    setRemainingDueButtons((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  // Auto-swap: when a button's position changes to a slot that another button
  // already occupies, swap the two so the visible positions stay unique
  // (1, 2, 3). "exclude" is treated as a wildcard slot — multiple buttons can
  // be excluded simultaneously without auto-swap, so toggling one button to
  // Exclude doesn't accidentally bring a previously-excluded button back.
  const updateRemainingDueButtonPosition = (
    id: RemainingDueButtonId,
    newPosition: 1 | 2 | 3 | "exclude"
  ) => {
    setRemainingDueButtons((prev) => {
      const oldPosition = prev[id].position;
      if (oldPosition === newPosition) return prev;
      const next: RemainingDueButtons = {
        ...prev,
        [id]: { ...prev[id], position: newPosition },
      };
      // No swap when moving TO Exclude — Exclude is shared, not unique.
      if (newPosition === "exclude") return next;
      const swapId = REMAINING_DUE_BUTTON_IDS.find(
        (other) => other !== id && prev[other].position === newPosition
      );
      if (swapId) {
        next[swapId] = { ...prev[swapId], position: oldPosition };
      }
      return next;
    });
  };

  const subEventRef = useRef<SubEventManagerHandle>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [showSubEventWarning, setShowSubEventWarning] = useState(false);
  const bypassSubEventCheck = useRef(false);

  const handlePriceChange = (value: number) => {
    setServiceFeePerOrder(value);
  };
  const handlePriceChangeTopUp = (value: number) => {
    setServiceFeePerOrderTopUp(value);
  };

  const handleThumbReady = async (blob: Blob) => {
    const dataUrl = await blobToDataURL(blob);
    const rawBase64 = dataUrlToRawBase64(dataUrl);
    setEventLogoBase64(rawBase64);
  };

  async function blobToDataURL(blob: Blob): Promise<string> {
    return await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result));
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
  }

  function dataUrlToRawBase64(dataUrl: string): string {
    const [, raw = ""] = dataUrl.split(",", 2);
    return raw;
  }

  function handleClearImage() {
    setEventLogoBase64("");
  }
  return (
    <Form
      ref={formRef}
      method="put"
      action={`/events/${event.id}/edit`}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
        }
      }}
      onSubmit={(e) => {
        if (bypassSubEventCheck.current) {
          bypassSubEventCheck.current = false;
          return;
        }
        if (subEventRef.current?.hasUncommittedData()) {
          e.preventDefault();
          setShowSubEventWarning(true);
        }
      }}
    >

        <div className="flex">
          <div className="ml-auto py-2 flex items-center gap-2">
            {getUserStore().user?.role_id === 1 && (
              <Link
                to={`/event-summary?eventId=${event.id}`}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
              >
                View Summary
              </Link>
            )}
            <TertiaryButton onClick={handleClone}>Clone Event</TertiaryButton>
            {deletePermission && (
            <DeleteModal
              onConfirm={() => {
                fetcher.submit(
                  {},
                  {
                    method: "delete",
                    action: `/events/${event.id}/delete`,
                  }
                );
              }}
            >
              {({ setOpen }) => (
                <SecondaryButton onClick={() => setOpen(true)}>
                  Delete
                </SecondaryButton>
              )}
            </DeleteModal>
            )}
          </div>
        </div>

      <FormLayout>
        <div className="col-span-3">
          <Input label="Event Name" name="name" defaultValue={event.name} />
        </div>
        <div className="col-span-1">
          <IndeterminateCheckbox
            label="Is Active?"
            name="is_active"
            defaultChecked={event.is_active}
          />
        </div>
        <div className="col-span-2">
          <IndeterminateCheckbox
            label="Hide from POS"
            name="cashless_hide_from_pos_dropdown"
            defaultChecked={event.cashless_configurations?.hide_from_pos_dropdown ?? false}
          />
        </div>
        {permissions === "admin" && (
          <div className="col-span-6">
            <CustomSelect
              title="Organization"
              name="organization_id"
              required
              defaultValue={event?.organization_id}
              options={organizations.map((org) => ({
                label: org.name,
                value: org.id,
              }))}
            />
          </div>
        )}
        {permissions !== "admin" && (
          <input
            type="hidden"
            value={event?.organization_id ?? ""}
            name="organization_id"
          />
        )}
        <div className="col-span-6">
          <Tab
              labels={[
                "Event Information",
                "Features",
                "Payments Configuration",
                "Cashless Configuration",
                "Ticketing Configuration",
                "Invoicing Configuration",
                "Web-Checkout Configuration",
                // Hidden while SHOW_INVENTORY_CONFIGURATION_TAB is false — see
                // the kill-switch comment at the top of this file.
                ...(SHOW_INVENTORY_CONFIGURATION_TAB
                  ? ["Inventory Configuration"]
                  : []),
                "Sub-Events",
              ]}
          >
            {/* Tab 1: Event Info */}
            <div className="grid grid-cols-6 gap-5">
              <div className="col-span-3">
                <DatePicker
                    label="Start Date"
                    name="start_date"
                    defaultValue={defaultStartDate}
                />
              </div>
              <div className="col-span-3">
                <DatePicker
                    label="End Date"
                    name="end_date"
                    defaultValue={defaultEndDate}
                />
              </div>
              <div className="col-span-3">
                <Input
                    label="Start Time"
                    name="start_time"
                    defaultValue={event.start_time}
                />
              </div>
              <div className="col-span-3">
                <Input
                    label="End Time"
                    name="end_time"
                    defaultValue={event.end_time}
                />
              </div>
              <div className="col-span-6">
                <CustomSelect
                    title="Timezone"
                    required
                    name="timezone"
                    defaultValue={event.timezone}
                    options={[
                      { value: "ADT", label: "Atlantic Daylight Time" },
                      { value: "AST", label: "Atlantic Standard Time" },
                      { value: "EDT", label: "Eastern Daylight Time" },
                      { value: "EST", label: "Eastern Standard Time" },
                      { value: "CDT", label: "Central Daylight Time" },
                      { value: "CST", label: "Central Standard Time" },
                      { value: "MDT", label: "Mountain Daylight Time" },
                      { value: "MST", label: "Mountain Standard Time" },
                      { value: "PDT", label: "Pacific Daylight Time" },
                      { value: "PST", label: "Pacific Standard Time" },
                    ]}
                />
              </div>
              <div className="col-span-3">
                <Input
                    label="Street Address"
                    name="address_street"
                    defaultValue={event.address_street}
                />
              </div>
              <div className="col-span-3">
                <Input
                    label="Street Address 2"
                    name="address_street_2"
                    defaultValue={event.address_street_2}
                />
              </div>
              <div className="col-span-3">
                <Input
                    label="City"
                    name="address_city"
                    defaultValue={event.address_city}
                />
              </div>
              <div className="col-span-1">
                <Input
                    label="State"
                    name="address_state"
                    defaultValue={event.address_state}
                />
              </div>
              <div className="col-span-2">
                <Input
                    label="Zipcode"
                    name="address_zipcode"
                    defaultValue={event.address_zipcode}
                />
              </div>
            </div>
            {/* Tab 2: Feature Configuration */}
            <div className="grid grid-cols-6 gap-5">
              <div className="col-span-6">
                <TagsInput
                    label="Event Zones"
                    name="location_zones"
                    placeholder="Add a Zone Name (press 'enter' to add)"
                    defaultValue={event.location_zones}
                />
              </div>
              <div className="col-span-2">
                <IndeterminateCheckbox
                    label="Password Protected Organizer Logout"
                    name="is_org_logout_protected"
                    defaultChecked={event?.is_org_logout_protected}
                    onChange={(checked) => setLogoutProtected(checked as boolean)}
                />
              </div>
              <div className="col-span-4">
                <IndeterminateCheckbox
                    label="Password Protected Clerk Logout"
                    name="is_clerk_logout_protected"
                    defaultChecked={event?.is_clerk_logout_protected}
                    onChange={(checked) => setLogoutProtected(checked as boolean)}
                />
              </div>
              {eventLogoBase64 && (
                <div className="col-span-3 sm:col-span-1">
                  <div className="relative w-[125px] h-[125px]">
                    <img
                      src={`data:image/webp;base64,${eventLogoBase64}`}
                      width={125}
                      height={125}
                      alt="event logo"
                      className="rounded border border-slate-200 object-cover"
                    />
                    <button
                      type="button"
                      onClick={handleClearImage}
                      aria-label="Remove image"
                      title="Remove image"
                      className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#f04345] text-white
                        flex items-center justify-center shadow ring-1 ring-white/60 hover:opacity-90
                        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#f04345]"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
              <div className="col-span-3 sm:col-span-2">
                <h4 className="block text-md font-medium text-gray-700">Event Logo (POS App)
                  <CompactThumbUploader onReady={handleThumbReady} variant={'landscape'} />
                </h4>
              </div>
              <div className="col-span-6 sm:col-span-2">
                <CustomSelect
                  title="Image from Library"
                  name="image_library"
                  value={eventLogoBase64}
                  onChange={(v) => {
                    setEventLogoBase64(v);
                  }}
                  options={imageOptions}
                />
              </div>
            </div>
            {/* Tab 3: Payment Configuration */}
            <div className="grid grid-cols-6 gap-5">
              <div className="col-span-6">
                <CustomSelect
                    name="payment_processor_id"
                    title="Payment Processor"
                    defaultValue={event.payment_processor_id}
                    options={[
                      { label: "PENDING", value: "" },
                      ...sortedPaymentProcessors.map((p) => ({
                        value: p.id,
                        label: p.payment_processor,
                      })),
                    ]}
                />
              </div>
              <>
                <div className="col-span-6">
                  <Input
                      label="Processor Dynamic Descriptor"
                      placeholder="Max 22 Characters"
                      name="dynamic_descriptor"
                      defaultValue={event.dynamic_descriptor}
                      maxLength={22}
                      sanitize={/[^a-zA-Z0-9 ]/g}
                  />
                </div>
              </>
              <div className="col-span-6">
                <CustomSelect
                    title="Currency"
                    name="currency"
                    defaultValue={event.currency}
                    options={[{ value: "USD", label: "USD" }]}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Service Fee Label"
                    name="digital_surcharge_label"
                    defaultValue={event.digital_surcharge_label}
                />
              </div>
              <div className="col-span-6 mt-4">
                <p className="block text-sm font-semibold text-gray-800 mb-1">
                  Remaining Due Section
                </p>
                <p className="block text-xs text-gray-500 mb-3">
                  Customize the buttons shown on the cashless Remaining Due
                  screen in the POS app. Position uses auto-swap — moving one
                  button to a slot pushes the previous occupant out.
                </p>
                <div className="space-y-3">
                  {REMAINING_DUE_BUTTON_IDS.map((id) => {
                    const btn = remainingDueButtons[id];
                    return (
                      <div
                        key={id}
                        className="border border-gray-200 rounded-md p-3"
                      >
                        <p className="text-sm font-medium text-gray-700 mb-2">
                          {REMAINING_DUE_BUTTON_DISPLAY_NAMES[id]}
                        </p>
                        <div className="grid grid-cols-12 gap-3">
                          <div className="col-span-12 sm:col-span-6">
                            <Input
                              label="Label"
                              name={`remaining_due_btn_${id}_label`}
                              value={btn.label}
                              onChange={(v) =>
                                updateRemainingDueButtonField(id, "label", String(v ?? ""))
                              }
                            />
                          </div>
                          <div className="col-span-6 sm:col-span-3">
                            <CustomSelect
                              title="Color"
                              value={btn.color}
                              onChange={(v) =>
                                updateRemainingDueButtonField(id, "color", v)
                              }
                              options={[
                                { label: "Red", value: "red" },
                                { label: "Black", value: "black" },
                              ]}
                            />
                          </div>
                          <div className="col-span-6 sm:col-span-3">
                            <CustomSelect
                              title="Position"
                              value={btn.position}
                              onChange={(v) =>
                                updateRemainingDueButtonPosition(
                                  id,
                                  v === "exclude"
                                    ? "exclude"
                                    : (Number(v) as 1 | 2 | 3)
                                )
                              }
                              options={[
                                { label: "Top", value: 1 },
                                { label: "Middle", value: 2 },
                                { label: "Bottom", value: 3 },
                                { label: "Exclude (Hide Button)", value: "exclude" },
                              ]}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <input
                  type="hidden"
                  name="cashless_remaining_due_buttons"
                  value={JSON.stringify(remainingDueButtons)}
                />
              </div>
            </div>
            {/* Tab 4: Cashless Configuration */}
            <div className="grid grid-cols-6 gap-5">
              <div className="col-span-6">
                <Input
                    label="Frontgate Site Id"
                    name="frontgate_site_id"
                    defaultValue={event.frontgate_site_id}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Cashless URL Slug"
                    name="slug"
                    value={slugInputValue}
                    disallowUnderscore={true}
                    onChange={(value) => setSlugInputValue(value as string)}
                />
              </div>
              <div className="block text-sm font-medium text-gray-700 col-span-6">
                <a
                    href={`https://cashless.roninpos.app/events/${slugInputValue}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link-underline"
                >
                  https://cashless.roninpos.app/events/{slugInputValue}
                </a>
                <br />
                {/*
                  The attendee's cash refund page. It lives on the same cashless
                  app and is reached by the slug above, so people look for it
                  here rather than in Refund Manager — which is staff-only and
                  does not hand out this address.

                  Shown whether or not refunds are on, because the link is a
                  property of the event, not of the setting. The note says which
                  it is, so nobody prints a QR code for a page that will turn
                  every attendee away.
                */}
                <a
                    href={`https://cashless.roninpos.app/events/${slugInputValue}/refund`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link-underline"
                >
                  https://cashless.roninpos.app/events/{slugInputValue}/refund
                </a>
                <span className="ml-2 font-normal text-gray-500">
                  {(event.cashless_configurations?.cash_refund as { enabled?: boolean } | undefined)
                    ?.enabled
                    ? "— cash refunds, open once the event has ended"
                    : "— cash refunds (turned off for this event)"}
                </span>
                <br />
              </div>
              <div className="col-span-6">
                <Input
                    label="Custom Header"
                    name="cashless_registration_header"
                    defaultValue={event.cashless_configurations?.registration_header ?? ""}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Cashless Logo URL"
                    name="logo"
                    defaultValue={event.logo}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Cashless Background URL"
                    name="background_url"
                    defaultValue={event.background_url}
                />
              </div>
              <div className="col-span-6">
                <TagsInput
                    label="Eligible Token Types"
                    name="available_tokens"
                    placeholder="Add a Token Type (press 'enter' to add)"
                    defaultValue={event.available_tokens}
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <IndeterminateCheckbox
                    label="Open-Loop Cashless Enabled"
                    name="open_loop_cashless"
                    defaultChecked={event.open_loop_cashless}
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <IndeterminateCheckbox
                    label="Cashless Transfer/Combine Enabled"
                    name="is_asset_transfer_combine"
                    defaultChecked={event.is_asset_transfer_combine}
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <IndeterminateCheckbox
                    label="Cashless Top-Up Sales Enabled"
                    name="cashless_top_up_sales"
                    defaultChecked={event.cashless_top_up_sales}
                />
              </div>
              <div className="col-span-1">
                <IndeterminateCheckbox
                    label="PIN on First Association Only Enabled"
                    name="cashless_pin_on_association_only"
                    defaultChecked={event.cashless_configurations?.pin_on_association_only ?? false}
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <IndeterminateCheckbox
                    label="Require UID/PrintID on Registration Flow"
                    name="uid_reg_flow"
                    defaultChecked={event.uid_reg_flow}
                />
              </div>
              <div className="col-span-1">
                <IndeterminateCheckbox
                    label="Require Customer Details at Cash Loading (Required for Canada)"
                    name="require_cash_loading_customer_fields"
                    defaultChecked={event?.require_cash_loading_customer_fields}
                />
              </div>
              <div className="col-span-1">
                <IndeterminateCheckbox
                    label="Allow Vault Card Skip"
                    name="cashless_allow_vault_card_skip"
                    defaultChecked={event.cashless_configurations?.allow_vault_card_skip ?? false}
                />
              </div>
              <div className="col-span-1">
                <IndeterminateCheckbox
                    label="Enable downloadable, associated QR"
                    name="cashless_enable_downloadable_qr"
                    defaultChecked={event.cashless_configurations?.enable_downloadable_qr ?? false}
                />
              </div>
              {isRoninStaff && (
                <>
                  <div className="col-span-2 sm:col-span-1">
                    <input type="hidden" name="cash_refund_present" value="true" />
                    <input
                        type="hidden"
                        name="cash_refund_enabled"
                        value={cashRefundEnabled ? "true" : "false"}
                    />
                    <IndeterminateCheckbox
                        label="Cash Refunds Enabled"
                        name="cash_refund_enabled_visible"
                        defaultChecked={cashRefundEnabled}
                        onChange={(checked) => setCashRefundEnabled(checked as boolean)}
                    />
                  </div>

                  {/*
                    The settings appear only once refunds are on, and stay in the
                    form when they are switched off - hidden, not cleared - so a
                    stray click cannot lose an event's fee and minimum.
                  */}
                  {cashRefundEnabled && (
                    <div className="col-span-6 rounded-md border border-gray-200 bg-gray-50 p-4">
                      <div className="mb-3 text-sm font-medium text-gray-700">
                        Cash refund settings
                      </div>
                      <div className="grid grid-cols-6 gap-4">
                        <div className="col-span-6 sm:col-span-2">
                          <Input
                              label="Minimum refund ($)"
                              name="cash_refund_minimum"
                              defaultValue={centsToDollars(storedCashRefund?.min_amount ?? 100)}
                          />
                        </div>
                        <div className="col-span-6 sm:col-span-2">
                          <Input
                              label="Ronin fee per refund ($)"
                              name="cash_refund_fee"
                              defaultValue={centsToDollars(storedCashRefund?.fee_amount ?? 0)}
                          />
                        </div>
                        <div className="col-span-6 sm:col-span-2">
                          <Input
                              label="Banking days until the money lands"
                              name="cash_refund_effective_days"
                              type="number"
                              defaultValue={String(storedCashRefund?.ach?.effective_days ?? 1)}
                          />
                        </div>
                        <div className="col-span-6 sm:col-span-3">
                          <CustomSelect
                              title="ACH batch type"
                              name="cash_refund_sec_code"
                              defaultValue={
                                storedCashRefund?.ach?.sec_code === "PPD" ? "PPD" : "CCD"
                              }
                              options={[
                                { label: "CCD", value: "CCD" },
                                { label: "PPD (Chase requires the description PAYROLL)", value: "PPD" },
                              ]}
                          />
                        </div>
                        <div className="col-span-6 sm:col-span-3">
                          <Input
                              label="What the attendee sees on their statement (10 characters)"
                              name="cash_refund_entry_description"
                              defaultValue={String(
                                storedCashRefund?.ach?.entry_description ?? "REFUND"
                              )}
                          />
                        </div>

                        {/*
                          Wording on the attendee's refund page. Left blank, the
                          page uses its own text - so these are an override, not
                          a requirement.
                        */}
                        <div className="col-span-6 sm:col-span-3">
                          <Input
                              label="Refund page heading (optional)"
                              name="cash_refund_heading"
                              placeholder="Get Your Cash Back"
                              defaultValue={String(storedCashRefund?.copy?.heading ?? "")}
                          />
                        </div>
                        <div className="col-span-6">
                          <label className="block text-sm font-medium text-gray-700">
                            Refund page intro text (optional)
                          </label>
                          <textarea
                              name="cash_refund_intro"
                              rows={3}
                              placeholder="If you loaded cash onto your wristband at a booth and have money left over, we can send it to your bank account."
                              defaultValue={String(storedCashRefund?.copy?.intro ?? "")}
                              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                          />
                        </div>

                        {/*
                          The three numbered steps, and the box the attendee
                          types into. Every one is optional: blank keeps our
                          wording, so an organizer changes only what they care
                          about. The wristband picture stays on step one
                          whatever it says.
                        */}
                        <div className="col-span-6 sm:col-span-2">
                          <Input
                              label="Step 1 (optional)"
                              name="cash_refund_step_one"
                              placeholder="Locate the Unique Identifier on your wristband"
                              defaultValue={String(storedCashRefund?.copy?.step_one ?? "")}
                          />
                        </div>
                        <div className="col-span-6 sm:col-span-2">
                          <Input
                              label="Step 2 (optional)"
                              name="cash_refund_step_two"
                              placeholder="Enter it below to see what you have left"
                              defaultValue={String(storedCashRefund?.copy?.step_two ?? "")}
                          />
                        </div>
                        <div className="col-span-6 sm:col-span-2">
                          <Input
                              label="Step 3 (optional)"
                              name="cash_refund_step_three"
                              placeholder="Enter your bank details on the next page"
                              defaultValue={String(storedCashRefund?.copy?.step_three ?? "")}
                          />
                        </div>
                        <div className="col-span-6 sm:col-span-3">
                          <Input
                              label="Wristband box label (optional)"
                              name="cash_refund_uid_label"
                              placeholder="Wristband Unique Identifier"
                              defaultValue={String(storedCashRefund?.copy?.uid_label ?? "")}
                          />
                        </div>
                        <div className="col-span-6 sm:col-span-3">
                          <Input
                              label="Wristband box example (optional)"
                              name="cash_refund_uid_placeholder"
                              placeholder="e.g. 1234567"
                              defaultValue={String(storedCashRefund?.copy?.uid_placeholder ?? "")}
                          />
                        </div>
                      </div>
                      <p className="mt-3 text-xs text-gray-500">
                        Refunds open on their own once the event's end date has passed, and they are
                        not automatic: each one queues in Refund Manager, where somebody downloads
                        the Chase file and uploads it to the bank.
                      </p>
                    </div>
                  )}
                </>
              )}
              <div className="col-span-6">
                <PercentageInput
                    name="top_up_service_fee_percentage"
                    label="Cashless Top-Up Service Fee Value (%)"
                    defaultValue={event.top_up_service_fee_percentage}
                />
              </div>
              <div className="col-span-6">
                <PriceInput
                    label="Cashless Top-Up Service Fee Per Order"
                    name="top_up_service_fee_per_order"
                    onChange={(value) => handlePriceChangeTopUp(value ? value : 0)}
                    value={serviceFeePerOrderTopUp}
                />
              </div>
              <DemographicQuestionsBuilder defaultValue={event.signup_questions} />
            </div>
            {/* Tab 5: Ticketing */}
            <div className="grid grid-cols-6 gap-5">
              <div className="col-span-6">
                <CustomSelect
                    name="ticketing_payment_processor_id"
                    title="Ticketing Payment Processor"
                    defaultValue={event.ticketing_payment_processor_id}
                    options={[
                      { label: "PENDING", value: "" },
                      ...sortedPaymentProcessors.map((p) => ({
                        value: p.id,
                        label: p.payment_processor,
                      })),
                    ]}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Ticketing URL Slug"
                    name="ticketing_slug"
                    value={ticketingSlugInputValue}
                    onChange={(value) => setTicketingSlugInputValue(value as string)}
                />
              </div>
              <div className="block text-sm font-medium text-gray-700 col-span-6">
                <a
                    href={`https://ticketing.roninpos.app/events/${ticketingSlugInputValue}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link-underline"
                >
                  https://ticketing.roninpos.app/events/{ticketingSlugInputValue}
                </a>
              </div>
              <div className="col-span-6">
                <Input
                    label="Ticketing Logo URL"
                    name="ticketing_logo"
                    defaultValue={event.ticketing_logo}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Ticketing Background URL"
                    name="ticketing_background_url"
                    defaultValue={event.ticketing_background_url}
                />
              </div>
              <div className="col-span-6">
                <PercentageInput
                    name="web_surcharge_percentage"
                    label="Ticketing Site Service Fee Value (%)"
                    defaultValue={event.web_surcharge_percentage}
                />
              </div>
              <div className="col-span-6">
                <PriceInput
                    label="Ticketing Site Service Fee Per Order"
                    name="ticketing_service_fee_per_order"
                    onChange={(value) => handlePriceChange(value ? value : 0)}
                    value={serviceFeePerOrder}
                />
              </div>
              <div className="col-span-6">
                <TicketingItemHTML
                  content={descriptionContent}
                  setContent={setDescriptionContent}
                  label="Ticketing Event Description (Shows on Ticketing Sites)"
                />
                <Input type="hidden" name="description" value={descriptionContent} />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <IndeterminateCheckbox
                    label="Tickets Remaining Call-To-Action?"
                    name="ticketing_show_remaining_cta"
                    defaultChecked={event.ticketing_configurations?.show_remaining_cta ?? true}
                />
              </div>
            </div>
            {/* Tab 6: Invoicing */}
            <div className="grid grid-cols-6 gap-5">
              <div className="col-span-6">
                <CustomSelect
                    name="invoicing_payment_processor_id"
                    title="Invoicing Payment Processor"
                    defaultValue={event.invoicing_payment_processor_id}
                    options={[
                      { label: "PENDING", value: "" },
                      ...sortedPaymentProcessors.map((p) => ({
                        value: p.id,
                        label: p.payment_processor,
                      })),
                    ]}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Invoicing Logo URL"
                    name="invoicing_logo"
                    defaultValue={event.invoicing_logo}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Invoicing Background URL"
                    name="invoicing_background_url"
                    defaultValue={event.invoicing_background_url}
                />
              </div>
            </div>
            {/* Tab 7: Web-Checkout Configuration */}
            <div className="grid grid-cols-6 gap-5">
              <div className="col-span-6">
                <CustomSelect
                    name="web_checkout_payment_processor_id"
                    title="Web-Checkout Payment Processor"
                    defaultValue={event.web_checkout_configurations?.payment_processor_id ? Number(event.web_checkout_configurations.payment_processor_id) : null}
                    options={[
                      { label: "PENDING", value: "" },
                      ...sortedPaymentProcessors.map((p) => ({
                        value: p.id,
                        label: p.payment_processor,
                      })),
                    ]}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Web-Checkout URL Slug"
                    name="web_checkout_slug"
                    value={webCheckoutSlugInputValue}
                    onChange={(value) => setWebCheckoutSlugInputValue(value as string)}
                />
              </div>
              <div className="block text-sm font-medium text-gray-700 col-span-6">
                <a
                    href={`https://web-checkout.roninpos.app/clients/${webCheckoutSlugInputValue}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link-underline"
                >
                  https://checkout.roninpos.app/events/{webCheckoutSlugInputValue}
                </a>
              </div>
              <div className="col-span-6">
                <Input
                    label="Web-Checkout Logo URL"
                    name="web_checkout_logo"
                    defaultValue={event.web_checkout_configurations?.logo}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Web-Checkout Background URL"
                    name="web_checkout_background_url"
                    defaultValue={event.web_checkout_configurations?.background_url}
                />
              </div>
              <div className="col-span-6">
                <PercentageInput
                    name="web_checkout_service_fee_percentage"
                    label="Web-Checkout Service Fee Value (%)"
                    defaultValue={event.web_checkout_configurations?.service_fee_percentage ?? 0}
                />
              </div>
              <div className="col-span-6">
                <IndeterminateCheckbox
                    label="Collect Client Information (name, phone, email — uncheck for amount-only checkout)"
                    name="web_checkout_collect_client_info"
                    defaultChecked={event.web_checkout_configurations?.collect_client_info !== false}
                />
              </div>
              <div className="col-span-6">
                <TicketingItemHTML
                    content={webCheckoutDescriptionContent}
                    setContent={setWebCheckoutDescriptionContent}
                    label="Web-Checkout Description (Shows on Web Checkout Site)"
                />
                <input type="hidden" name="web_checkout_description" value={webCheckoutDescriptionContent} />
              </div>
            </div>
            {/* Tab 8: Inventory Configuration (Merch Manager).
                Rendered only when SHOW_INVENTORY_CONFIGURATION_TAB is true —
                see the kill-switch comment at the top of this file. When
                hidden, the merch inputs are absent from the form's FormData,
                and eventEditAction's `merchSettingsSubmitted` guard skips the
                merch `_append` entirely, so existing configured values are
                never cleared by saving an event while the tab is off. */}
            {SHOW_INVENTORY_CONFIGURATION_TAB && (
            <div className="grid grid-cols-6 gap-5">
              <div className="col-span-6">
                <CustomSelect
                    title="Merch Payout Source"
                    name="merch_payout_source"
                    defaultValue={event.configuration?.merch_payout_source ?? ""}
                    options={[
                      { value: "", label: "Not set — merch payouts blocked" },
                      { value: "festival", label: "Organizer's Stripe account" },
                      { value: "ronin", label: "Ronin's Stripe account" },
                    ]}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Which Stripe account pays artists in Merch Manager. "Organizer's" debits the
                  account connected to this event's payment processor; "Ronin's" transfers from
                  the platform account. Settlements freeze this value when they lock, so changing
                  it only affects settlements locked afterwards.
                </p>
              </div>

              <div className="col-span-6 sm:col-span-3">
                <Input
                    label="Merch Tax % ( 7.5 = 7.5% )"
                    name="merch_tax_percentage"
                    defaultValue={
                      event.configuration?.merch_tax_percentage ?? 0
                    }
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <CustomSelect
                    title="Merch Tax Type"
                    name="merch_tax_type"
                    defaultValue={event.configuration?.merch_tax_type ?? "inclusive"}
                    options={[
                      { label: "Inclusive (tax is inside the ticket price)", value: "inclusive" },
                      { label: "Exclusive (tax added at checkout)", value: "exclusive" },
                    ]}
                />
              </div>
              <div className="col-span-6">
                <p className="mt-1 text-xs text-gray-500">
                  Applied to every item created through Merch Manager, so artists and vendor
                  managers never set tax themselves. Existing items are unaffected — change tax on
                  an individual item from the Items screen. 0% marks items as non-taxed.
                </p>
              </div>
              {/* Per-artist revenue splits + fee lines. NOT part of this form:
                  they save themselves through their own mutations, and none of
                  their inputs carry a `name` so nothing leaks into the event
                  submit. Hidden until the event has an id (create/clone flows)
                  because every row is keyed on (event_id, vendor_id). */}
              {event?.id ? (
                <MerchVendorDeals
                  eventId={event.id}
                  organizationId={event.organization_id}
                />
              ) : null}
            </div>
            )}
            {/* Tab 9: Sub-Events */}
            <div className="grid grid-cols-6 gap-5">
              <SubEventManager ref={subEventRef} defaultValue={event.sub_events} />
            </div>
          </Tab>
        </div>
        <input
          type="hidden"
          value={eventLogoBase64}
          name="event_logo_base64"
        />
      </FormLayout>

      <Transition.Root show={showSubEventWarning} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={setShowSubEventWarning}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                      <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                        Uncommitted Sub-Event
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          You have data entered in the sub-event form that hasn't been added. Would you like to go back and add it, or save without it?
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                      onClick={() => {
                        setShowSubEventWarning(false);
                        bypassSubEventCheck.current = true;
                        formRef.current?.requestSubmit();
                      }}
                    >
                      Save Without Adding
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                      onClick={() => setShowSubEventWarning(false)}
                    >
                      Go Back
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </Form>
  );
};
